import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Link,
  Spinner,
  Switch,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import { apiGetBaraPostBindContext, apiBaraPostBindReview } from 'api';
import { AuthContext } from 'contexts/AuthContext';
import { JIRA_BASE_URL, ROLE_UNDERWRITER } from 'variables/constants';
import type { APIBaraPostBindContext } from 'types/api.types';

const BaraPostBindReviewView = () => {
  const { referralId } = useParams<{ referralId: string }>();
  const { user } = useContext(AuthContext) as { user: { role: number } };
  const isUW = user?.role === ROLE_UNDERWRITER;

  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [context, setContext] = useState<APIBaraPostBindContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSio, setIsSio] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);

  const fetchContext = useCallback(async () => {
    if (!referralId) return;
    setLoading(true);
    try {
      const data = (await apiGetBaraPostBindContext(
        referralId,
      )) as APIBaraPostBindContext;
      setContext(data);
      setIsSio(data.is_sio);
      setReviewNotes(data.post_bind_review_notes || '');
      if (data.post_bind_reviewed_at) setCompleted(true);
    } catch {
      toastRef.current({
        title: 'Failed to load review context',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [referralId]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  const handleCompleteReview = async () => {
    if (!referralId) return;
    setSaving(true);
    try {
      await apiBaraPostBindReview(referralId, {
        is_sio: isSio,
        post_bind_review_notes: reviewNotes,
      });
      toastRef.current({
        title: 'Post-bind review completed',
        status: 'success',
        duration: 3000,
      });
      setCompleted(true);
      await fetchContext();
    } catch {
      toastRef.current({
        title: 'Failed to save review',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaraFeatureGuard>
      <Box
        pt={{ base: '130px', md: '80px' }}
        px={{ base: 4, md: 6 }}
        maxW="800px"
        mx="auto"
      >
        <Heading size="md" mb={2}>
          Post-Bind Quality Review
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={6}>
          {isUW
            ? 'Review the BARA referral details, flag Service Improvement Opportunities (SIO), and record your quality check notes.'
            : 'Read-only view of the post-bind quality review status for this referral.'}
        </Text>

        {loading ? (
          <Flex justify="center" py={16}>
            <Spinner size="sm" mr={3} />
            <Text color="gray.500">Loading…</Text>
          </Flex>
        ) : !context ? (
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>
              Referral not found or not yet approved.
            </AlertDescription>
          </Alert>
        ) : (
          <VStack align="stretch" spacing={5}>
            {completed && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  Review completed by {context.post_bind_reviewed_by || 'you'}{' '}
                  on{' '}
                  {context.post_bind_reviewed_at
                    ? new Date(context.post_bind_reviewed_at).toLocaleString()
                    : '—'}
                </AlertDescription>
              </Alert>
            )}

            {/* Submission context */}
            <Card p={5}>
              <Text
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wider"
                color="gray.400"
                mb={4}
              >
                Submission Details
              </Text>
              <Grid templateColumns="1fr 1fr" gap={4} mb={4}>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Broker
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {context.broker_name}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {context.broker_email}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Market
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {context.market || '—'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Effective Date
                  </Text>
                  <Text fontSize="sm">{context.effective_date || '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Submitted
                  </Text>
                  <Text fontSize="sm">
                    {new Date(context.submitted_at).toLocaleString()}
                  </Text>
                </Box>
              </Grid>

              <Divider mb={4} />

              <Text
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wider"
                color="gray.400"
                mb={3}
              >
                MP/Delegate Approval
              </Text>
              <Grid templateColumns="1fr 1fr" gap={4} mb={3}>
                <Box>
                  <Text fontSize="xs" color="gray.400">
                    Approved By
                  </Text>
                  <Text fontSize="sm">
                    {context.approved_by_name ||
                      context.approved_by_email ||
                      '—'}
                  </Text>
                </Box>
                {context.bara_ticket_key && (
                  <Box>
                    <Text fontSize="xs" color="gray.400">
                      BARA Ticket
                    </Text>
                    <Link
                      href={`${JIRA_BASE_URL}/browse/${context.bara_ticket_key}`}
                      isExternal
                      fontSize="sm"
                      color="blue.500"
                    >
                      {context.bara_ticket_key} <ExternalLinkIcon boxSize={3} />
                    </Link>
                  </Box>
                )}
              </Grid>
              {context.justification && (
                <Box bg="gray.50" borderRadius="md" p={3}>
                  <Text fontSize="xs" color="gray.400" mb={1}>
                    Justification
                  </Text>
                  <Text fontSize="sm">{context.justification}</Text>
                </Box>
              )}

              {context.submission_id && context.policy_uuid && (
                <Box mt={3}>
                  <Link
                    href={`/admin/policies/submission/${context.submission_id}/${context.policy_uuid}/personal`}
                    isExternal
                    fontSize="sm"
                    color="blue.500"
                  >
                    View Full Submission <ExternalLinkIcon boxSize={3} />
                  </Link>
                </Box>
              )}
            </Card>

            {/* SIO flag */}
            <Card p={5}>
              <HStack justify="space-between" mb={3}>
                <Box>
                  <Text fontWeight="600" fontSize="sm">
                    Service Improvement Opportunity (SIO)
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {isUW
                      ? 'Mark this submission as a Service Improvement Opportunity (SIO) for follow-up review.'
                      : 'Service Improvement Opportunity (SIO) status set by the Underwriter.'}
                  </Text>
                </Box>
                <HStack>
                  <Switch
                    isChecked={isSio}
                    onChange={(e) => isUW && setIsSio(e.target.checked)}
                    colorScheme="orange"
                    isDisabled={!isUW || completed}
                  />
                  {isSio && <Badge colorScheme="orange">SIO</Badge>}
                </HStack>
              </HStack>
            </Card>

            {/* Review notes */}
            <Card p={5}>
              <Text fontWeight="600" fontSize="sm" mb={1}>
                Quality Check Notes
              </Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                {isUW
                  ? 'Record your post-bind quality review findings. These are saved to the BARA referral record.'
                  : 'Notes recorded by the Underwriter during post-bind quality review.'}
              </Text>
              {!isUW && !reviewNotes ? (
                <Text fontSize="sm" color="gray.400" fontStyle="italic">
                  No notes recorded yet.
                </Text>
              ) : (
                <Textarea
                  placeholder="Enter quality check notes, observations, or next steps…"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={5}
                  fontSize="sm"
                  isDisabled={!isUW || completed}
                />
              )}
            </Card>

            {isUW && !completed && (
              <Flex justify="flex-end">
                <Button
                  colorScheme="green"
                  isLoading={saving}
                  onClick={handleCompleteReview}
                  size="md"
                >
                  Complete Review
                </Button>
              </Flex>
            )}
          </VStack>
        )}
      </Box>
    </BaraFeatureGuard>
  );
};

export default BaraPostBindReviewView;
